import React, { useMemo, useState } from 'react';
import NumericInput from './NumericInput';
import {
  QUARTER_RATE_MAX,
  QUARTER_RATE_MIN,
  SEASONALITY_PRESETS,
  effectiveAnnualizedRate,
  normalizeQuarterConfig,
  remainingYearQuarterMonthCounts,
  resolveQuarterValues
} from './forecastQuarterlyGrowth';

const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];
const FULL_YEAR_MONTH_COUNTS = [3, 3, 3, 3];

const ForecastQuarterlyGrowthDetail = ({
  def,
  yearAdjustments,
  quarterlyGrowth,
  onChange,
  remainderYear,
  latestActualMonth,
  selectedPeriod,
  onSelectedPeriodChange,
  disabled
}) => {
  // The workspace controls this value to persist accordion state; the fallback
  // keeps the reusable detail usable in standalone read-only previews.
  const [localPeriod, setLocalPeriod] = useState('roy');
  const period = selectedPeriod ?? localPeriod;
  const setPeriod = onSelectedPeriodChange || setLocalPeriod;
  const periodOptions = useMemo(() => [
    { id: 'roy', label: `RoY ${remainderYear}` },
    ...yearAdjustments.map((_, index) => ({
      id: String(remainderYear + 1 + index),
      label: `FY${remainderYear + 1 + index}`
    }))
  ], [remainderYear, yearAdjustments]);
  const storedConfig = quarterlyGrowth?.[def.id]?.[period];
  const config = normalizeQuarterConfig(storedConfig);
  const isRemainder = period === 'roy';
  const annualTarget = isRemainder
    ? config.annualTarget ?? 0
    : Number(yearAdjustments[Number(period) - (remainderYear + 1)]) || 0;
  const monthCounts = isRemainder
    ? remainingYearQuarterMonthCounts(latestActualMonth)
    : FULL_YEAR_MONTH_COUNTS;
  const values = resolveQuarterValues({ annualTarget, config, monthCounts });
  const effectiveRate = effectiveAnnualizedRate(values, monthCounts);
  const hasOverrides = config.overrides.some((value, index) => value !== null && monthCounts[index] > 0);
  const activeQuarterCount = monthCounts.filter((count) => count > 0).length;
  const allActiveOverridden = activeQuarterCount > 0 && config.overrides.filter(
    (value, index) => value !== null && monthCounts[index] > 0
  ).length === activeQuarterCount;
  const tiesToTarget = effectiveRate !== null && Math.abs(effectiveRate - annualTarget) < 0.0001;
  const effectiveRateLabel = effectiveRate === null ? '—' : effectiveRate.toFixed(2);

  const commit = (nextConfig, action) => onChange(
    def.id,
    period,
    nextConfig,
    `${def.label} · ${period === 'roy' ? `RoY ${remainderYear}` : `FY${period}`} · ${action}`
  );

  return (
    <div className="quarterly-growth-detail" id={`quarterly-detail-${def.id}`}>
      <div className="quarterly-growth-toolbar">
        <div>
          <strong>Optional quarterly detail</strong>
          <small>
            {isRemainder
              ? 'Annualized adjustment points layered over the model’s monthly seasonality; blank detail applies 0 pts.'
              : 'Annualized growth adjustment points; blank detail inherits the annual lever.'}
          </small>
        </div>
        <label>
          <span>Detail period</span>
          <select
            value={period}
            onChange={(event) => setPeriod(event.target.value)}
            aria-label={`${def.label} quarterly detail period`}
          >
            {periodOptions.map((option) => (
              <option key={option.id} value={option.id}>{option.label}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Seasonality</span>
          <select
            value={hasOverrides ? 'custom' : config.preset}
            disabled={disabled || activeQuarterCount === 0}
            onChange={(event) => {
              if (event.target.value === 'custom') return;
              commit({
                ...config,
                preset: event.target.value,
                overrides: [null, null, null, null]
              }, `${event.target.value} seasonality`);
            }}
            aria-label={`${def.label} seasonality`}
          >
            {SEASONALITY_PRESETS.map((preset) => (
              <option key={preset.id} value={preset.id}>{preset.label}</option>
            ))}
            {hasOverrides ? <option value="custom">Custom overrides</option> : null}
          </select>
        </label>
        {isRemainder ? (
          <label>
            <span>RoY annual target</span>
            <NumericInput
              ariaLabel={`${def.label} remaining year annualized target`}
              className="op3-stepper"
              value={annualTarget}
              min={def.min}
              max={def.max}
              step={def.step}
              disabled={disabled || activeQuarterCount === 0}
              onCommit={(value) => commit({ ...config, annualTarget: value }, 'annual target')}
            />
          </label>
        ) : (
          <div className="quarterly-growth-target" aria-label={`Annual target ${annualTarget} points`}>
            <span>Annual target</span>
            <strong>{annualTarget >= 0 ? '+' : ''}{annualTarget.toFixed(2)} pts</strong>
          </div>
        )}
        <label className="quarterly-preserve-target">
          <input
            type="checkbox"
            checked={config.preserveAnnual}
            disabled={disabled || activeQuarterCount === 0}
            onChange={(event) => commit({ ...config, preserveAnnual: event.target.checked }, 'preserve annual target')}
          />
          <span>Preserve annual target</span>
        </label>
        <button
          type="button"
          className="forecast-secondary-button compact"
          disabled={disabled || (!storedConfig && !hasOverrides)}
          onClick={() => commit({
            preset: 'even', preserveAnnual: true, annualTarget: isRemainder ? 0 : null,
            overrides: [null, null, null, null]
          }, 'reset quarterly detail')}
        >
          Reset Detail
        </button>
      </div>

      <div className="quarterly-growth-grid">
        {QUARTERS.map((quarter, index) => {
          const closed = monthCounts[index] <= 0;
          const partial = isRemainder && monthCounts[index] > 0 && monthCounts[index] < 3;
          return (
            <label className={closed ? 'closed' : ''} key={quarter}>
              <span>{quarter}{partial ? ' · partial' : ''}</span>
              {closed ? (
                <strong>Actual</strong>
              ) : (
                <div className="quarterly-growth-input">
                  <NumericInput
                    ariaLabel={`${def.label} ${period === 'roy' ? remainderYear : period} ${quarter}`}
                    className="op3-stepper"
                    value={Number(values[index]?.toFixed(3))}
                    min={QUARTER_RATE_MIN}
                    max={QUARTER_RATE_MAX}
                    step={def.step}
                    disabled={disabled}
                    onCommit={(value) => {
                      const overrides = [...config.overrides];
                      overrides[index] = value;
                      commit({ ...config, overrides }, `${quarter} override`);
                    }}
                  />
                  {config.overrides[index] !== null ? (
                    <button
                      type="button"
                      className="quarterly-use-profile"
                      disabled={disabled}
                      aria-label={`${def.label} ${quarter} use seasonality profile`}
                      onClick={() => {
                        const overrides = [...config.overrides];
                        overrides[index] = null;
                        commit({ ...config, overrides }, `${quarter} use seasonality profile`);
                      }}
                    >
                      Use Profile
                    </button>
                  ) : null}
                </div>
              )}
              <small>{closed ? 'Closed actual quarter' : `${monthCounts[index]} forecast month${monthCounts[index] === 1 ? '' : 's'}`}</small>
            </label>
          );
        })}
      </div>

      <div className={`quarterly-growth-status ${config.preserveAnnual && !tiesToTarget ? 'warning' : ''}`} aria-live="polite">
        {activeQuarterCount === 0
          ? `No remaining forecast months in ${remainderYear}. Choose FY${remainderYear + 1} or a later year.`
          : config.preserveAnnual && allActiveOverridden && !tiesToTarget
          ? `All open quarters are overridden. Effective rate is ${effectiveRateLabel} pts; unlock a quarter or turn off preservation to redefine the annual target.`
          : config.preserveAnnual && !tiesToTarget
          ? `The annual target cannot be preserved within the ${QUARTER_RATE_MIN} to +${QUARTER_RATE_MAX} point quarterly range. Use Profile on an overridden quarter, adjust the target, or turn off preservation.`
          : config.preserveAnnual
          ? `Quarterly compounding ties to the ${annualTarget.toFixed(2)}-point annual target. Editing a quarter creates a custom profile and redistributes the remaining quarters.`
          : `Quarter overrides define an effective ${effectiveRateLabel}-point annualized adjustment.`}
      </div>
    </div>
  );
};

export default React.memo(ForecastQuarterlyGrowthDetail);
